<template>
  <div class="rankings-page">
    <header class="rankings-header">
      <v-btn icon class="back-btn" @click="goBack">
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <div class="rankings-title-wrap">
        <h1 class="rankings-title">{{ league }} — {{ weight }}</h1>
        <p class="rankings-subtitle">Full rankings</p>
      </div>
    </header>

    <div class="rankings-content">
      <div v-if="loading" class="skeleton-rankings-list">
        <skeleton-loader type="list-item" />
        <skeleton-loader type="list-item" />
        <skeleton-loader type="list-item" />
        <skeleton-loader type="list-item" />
        <skeleton-loader type="list-item" />
      </div>
      <div v-else class="rankings-list">
        <div
          v-for="(wrestler, index) in wrestlers"
          :key="wrestler.id"
          class="wrestler-row"
          @click="goToWrestler(wrestler.id)"
        >
          <span class="wrestler-rank">{{ index + 1 }}</span>
          <div class="wrestler-avatar-wrap">
            <img
              v-if="
                authenticated &&
                wrestler.data.Image &&
                wrestler.data.Image !== ''
              "
              :src="wrestler.data.Image"
              class="wrestler-avatar"
              alt=""
            />
            <img
              v-else
              :src="require('../assets/default-profile.png')"
              class="wrestler-avatar"
              alt=""
            />
          </div>
          <div class="wrestler-info">
            <span class="wrestler-name">
              {{ wrestler.data.FirstName }} {{ wrestler.data.LastName }}
            </span>
            <div
              class="wc-skill-bar skill-bar skill-bar--linear"
              :aria-label="
                'Skill rating ' +
                formatSkillLevel(
                  wrestler.data && wrestler.data.Skill != null
                    ? wrestler.data.Skill
                    : 0
                )
              "
            >
              <div class="skill-bar-linear-track">
                <div
                  class="skill-bar-linear-fill"
                  :style="{
                    width: weightSkillBarPercent(wrestler) + '%',
                  }"
                ></div>
              </div>
              <span class="skill-bar-label">{{
                formatSkillLevel(
                  wrestler.data && wrestler.data.Skill != null
                    ? wrestler.data.Skill
                    : 0
                )
              }}</span>
            </div>
            <span class="wrestler-record">
              ({{ wrestler.data.Wins }} – {{ wrestler.data.Losses }})
            </span>
            <span class="wrestler-meta">
              {{ wrestler.data.Grade }} Grade · {{ wrestler.data.School }}
            </span>
          </div>
          <span
            v-if="rankChange(wrestler, index) !== null"
            class="rank-change-badge"
            :class="rankChangeClass(wrestler, index)"
          >
            {{ rankChangeText(wrestler, index) }}
          </span>
          <v-icon class="wrestler-arrow" small>chevron_right</v-icon>
        </div>
        <div v-if="!loading && wrestlers.length === 0" class="empty-state-wrap">
          <p class="empty-state">No wrestlers in this weight class.</p>
          <p class="empty-state-hint">Add wrestlers from the main menu.</p>
        </div>
      </div>
    </div>
    <FloatingNav />
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
} from "firebase/firestore";
import FloatingNav from "../components/FloatingNav.vue";
import { sortWrestlersByRankingRules } from "../utils/rankingSort";
import {
  seasonFilterState,
  filterAthleteRowsBySeason,
  isAllSeasons,
} from "../utils/seasonFilter";
import { formatSkillLevel } from "../utils/formatSkillDisplay";
import {
  fetchLeagueSkillMinMax,
  skillBarLinearFillPercent as linearSkillBarFillPercent,
  computeEffectiveSkillBarRange,
  SKILL_BAR_MIN,
} from "../utils/skillBarLinear";
import { authState } from "../utils/authState";

export default {
  name: "WeightClassRankings",
  components: {
    FloatingNav,
    "skeleton-loader": () => import("../components/SkeletonLoader"),
  },
  data() {
    return {
      wrestlers: [],
      previousRanks: {}, // athleteId -> rank from last recalculation
      loading: true,
      leagueSkillMin: null,
      leagueSkillMax: null,
    };
  },
  computed: {
    authenticated() {
      return authState.user != null;
    },
    league() {
      const p = this.$route.params.league;
      if (p == null || p === "") return "";
      try {
        return decodeURIComponent(p).trim();
      } catch (_) {
        return String(p || "").trim();
      }
    },
    weight() {
      const p = this.$route.params.weight;
      if (p == null || p === "") return "";
      try {
        return decodeURIComponent(p).trim();
      } catch (_) {
        return String(p || "").trim();
      }
    },
    weightQueryValue() {
      const w = this.weight;
      if (w === "") return "";
      const num = Number(w);
      return w !== "" && !isNaN(num) ? num : w;
    },
    weightClassSkillNumbers() {
      return (this.wrestlers || [])
        .map((w) => {
          const s = w.data && w.data.Skill;
          return typeof s === "number" ? s : Number(s);
        })
        .filter((n) => Number.isFinite(n));
    },
    weightClassBarRange() {
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...this.weightClassSkillNumbers
      );
    },
  },
  watch: {
    "$route.params": {
      handler() {
        this.loadWrestlers();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadWrestlers();
    this._seasonUnwatch = this.$watch(
      () => seasonFilterState.selected,
      () => {
        this.loadWrestlers();
      }
    );
  },
  beforeDestroy() {
    if (this._seasonUnwatch) this._seasonUnwatch();
  },
  methods: {
    formatSkillLevel,
    weightSkillBarPercent(wrestler) {
      const s = wrestler.data && wrestler.data.Skill;
      const skill = typeof s === "number" ? s : Number(s);
      const val = Number.isFinite(skill) ? skill : SKILL_BAR_MIN;
      const { low, high } = this.weightClassBarRange;
      return linearSkillBarFillPercent(val, high, low);
    },
    async loadLeagueSkillBounds() {
      this.leagueSkillMin = null;
      this.leagueSkillMax = null;
      const league = this.league;
      if (!league) return;
      const db = getFirestore();
      try {
        const r = await fetchLeagueSkillMinMax(db, league);
        this.leagueSkillMin = r.min;
        this.leagueSkillMax = r.max;
      } catch (_e) {
        /* keep null */
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    goToWrestler(id) {
      this.$router.push("/wrestler/" + id);
    },
    async loadWrestlers() {
      if (!this.league || !this.weight) {
        this.wrestlers = [];
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
        this.loading = false;
        return;
      }
      this.loading = true;
      const db = getFirestore();
      const leagueVal = this.league;
      const weightVal = this.weightQueryValue;
      const weightStr = this.weight;
      const tryQuery = (wVal, useOrderBy) => {
        const constraints = [
          where("RankLeague", "==", leagueVal),
          where("RankWeightClass", "==", wVal),
        ];
        if (useOrderBy) constraints.push(orderBy("Skill", "desc"));
        const q = query(collection(db, "athletes"), ...constraints);
        return getDocs(q).then((snap) => {
          const rows = [];
          snap.forEach((docSnap) =>
            rows.push({ data: docSnap.data(), id: docSnap.id })
          );
          if (useOrderBy === false && rows.length)
            rows.sort((a, b) => (b.data.Skill || 0) - (a.data.Skill || 0));
          return rows;
        });
      };
      try {
        let rows = await tryQuery(weightVal, true).catch(() =>
          tryQuery(weightVal, false)
        );
        if (rows.length === 0 && weightVal !== weightStr) {
          rows = await tryQuery(weightStr, true).catch(() =>
            tryQuery(weightStr, false)
          );
        }
        rows = await sortWrestlersByRankingRules(db, rows);
        if (!isAllSeasons(seasonFilterState.selected)) {
          rows = await filterAthleteRowsBySeason(
            db,
            rows,
            seasonFilterState.selected
          );
        }
        this.wrestlers = rows;
        await this.loadLeagueSkillBounds();
        // Load previous rankings snapshot for "↑5" / "↓2" badges (from last view or last recalc)
        this.previousRanks = {};
        const snapshotId = `${(leagueVal || "").replace(
          /\//g,
          "_"
        )}__${(weightStr != null ? String(weightStr) : "").replace(
          /\//g,
          "_"
        )}`;
        try {
          const snapRef = doc(db, "rankingsSnapshots", snapshotId);
          const snapDoc = await getDoc(snapRef);
          if (snapDoc.exists() && snapDoc.data().ranks) {
            this.previousRanks = snapDoc.data().ranks;
          }
        } catch (_) {
          // no snapshot yet
        }
        // Save current order as snapshot so next visit can show rank-change badges
        if (rows.length > 0) {
          const ranks = {};
          rows.forEach((row, idx) => {
            ranks[row.id] = idx + 1;
          });
          setDoc(doc(db, "rankingsSnapshots", snapshotId), {
            league: leagueVal,
            weight: weightStr,
            ranks,
            updatedAt: serverTimestamp(),
          }).catch(() => {});
        }
      } catch (_) {
        this.wrestlers = [];
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
      } finally {
        this.loading = false;
      }
    },
    rankChange(wrestler, currentIndex) {
      const prev = this.previousRanks[wrestler.id];
      if (prev == null) return null;
      const current = currentIndex + 1;
      const diff = prev - current;
      return diff === 0 ? null : diff;
    },
    rankChangeClass(wrestler, currentIndex) {
      const d = this.rankChange(wrestler, currentIndex);
      if (d == null) return "";
      return d > 0 ? "rank-change-up" : "rank-change-down";
    },
    rankChangeText(wrestler, currentIndex) {
      const d = this.rankChange(wrestler, currentIndex);
      if (d == null) return "";
      return d > 0 ? `↑${d}` : `↓${Math.abs(d)}`;
    },
  },
};
</script>

<style scoped>
.rankings-page {
  min-height: 100%;
  background: #f5f5f7;
  padding-bottom: 2rem;
}

.rankings-header {
  background: linear-gradient(135deg, #c62828 0%, #b71c1c 100%);
  color: #fff;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

@media (min-width: 768px) {
  .rankings-header {
    padding: 1rem 1.5rem;
  }
}

.back-btn {
  color: #fff !important;
  flex-shrink: 0;
}

.rankings-title-wrap {
  min-width: 0;
}

.rankings-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

@media (min-width: 768px) {
  .rankings-title {
    font-size: 1.35rem;
  }
}

.rankings-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  opacity: 0.9;
}

.rankings-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem 0.75rem;
}

@media (min-width: 768px) {
  .rankings-content {
    padding: 1.5rem 1rem;
  }
}

.rankings-list {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 1rem;
  color: #6b7280;
  font-size: 0.95rem;
}

.wrestler-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s ease;
  min-height: 56px;
}

.wrestler-row:last-child {
  border-bottom: none;
}

.wrestler-row:hover {
  background: #fafafa;
}

.wrestler-rank {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  line-height: 1;
  box-sizing: border-box;
}

.wrestler-avatar-wrap {
  flex-shrink: 0;
  margin-right: 12px;
}

.wrestler-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
}

.wrestler-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wc-skill-bar.skill-bar--linear {
  width: 100%;
  max-width: none;
  min-width: 0;
  align-self: stretch;
  height: 14px;
  position: relative;
}

.wc-skill-bar .skill-bar-linear-track {
  width: 100%;
  height: 14px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  position: relative;
}

.wc-skill-bar .skill-bar-linear-fill {
  height: 100%;
  border-radius: 999px;
  min-width: 0;
  background: linear-gradient(
    90deg,
    #22c55e 0%,
    #a3e635 22%,
    #facc15 48%,
    #f97316 74%,
    #b91c1c 100%
  );
  transition: width 0.25s ease;
}

.wc-skill-bar .skill-bar-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.65rem;
  font-weight: 600;
  color: #111827;
  line-height: 1;
  pointer-events: none;
}

.wrestler-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.95rem;
}

.wrestler-record {
  color: #6b7280;
  font-size: 0.85rem;
}

.wrestler-meta {
  color: #9ca3af;
  font-size: 0.8rem;
}

.rank-change-badge {
  flex-shrink: 0;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 6px;
  margin-right: 8px;
}

.rank-change-up {
  background: #dcfce7;
  color: #166534;
}

.rank-change-down {
  background: #fee2e2;
  color: #b91c1c;
}

.wrestler-arrow {
  color: #d1d5db;
  flex-shrink: 0;
}

.empty-state-wrap {
  padding: 2rem 16px;
  text-align: center;
}

.empty-state {
  color: #374151;
  font-size: 0.95rem;
  margin: 0 0 4px;
}

.empty-state-hint {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
}

/* Mobile: touch-friendly rows */
@media (max-width: 767px) {
  .wrestler-row {
    min-height: 56px;
    padding: 14px 16px;
  }
}

/* Dark mode */
.theme--dark .rankings-page {
  background: #121212;
}
.theme--dark .rankings-list {
  background: #1e1e1e;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}
.theme--dark .wrestler-row {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .wrestler-row:hover {
  background: #2d2d2d;
}
.theme--dark .wrestler-rank {
  background: rgba(254, 242, 242, 0.12);
  color: #f87171;
}
.theme--dark .wrestler-name {
  color: #f3f4f6;
}
.theme--dark .wrestler-record {
  color: #d1d5db;
}
.theme--dark .wrestler-meta {
  color: #9ca3af;
}
.theme--dark .wrestler-arrow {
  color: #6b7280;
}
.theme--dark .wc-skill-bar .skill-bar-linear-track {
  background: #374151;
}
.theme--dark .wc-skill-bar .skill-bar-label {
  color: #f3f4f6;
}
.theme--dark .rank-change-up {
  background: rgba(34, 197, 94, 0.2);
  color: #86efac;
}
.theme--dark .rank-change-down {
  background: rgba(185, 28, 28, 0.25);
  color: #fca5a5;
}
.theme--dark .empty-state {
  color: #e5e7eb;
}
.theme--dark .empty-state-hint {
  color: #9ca3af;
}
</style>
